import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { fetchArticles, fetchRecipes, fetchVideos } from "@/lib/queries";
import { useReadArticles } from "@/hooks/use-read-articles";
import { useBookmarks } from "@/hooks/use-bookmarks";
import { useSession } from "@/lib/session";
import { supabase } from "@/integrations/supabase/client";
import { ArticleListRow } from "@/components/ArticleListRow";
import { ImageCardGrid, type ImageCardItem } from "@/components/ImageCardGrid";
import { PaywallBadge } from "@/components/PaywallBadge";
import { TOPIC_LABEL, type Topic } from "@/lib/topics";
import { detectFormat, formatLabel } from "@/lib/format-detector";
import { renderWithWikiLinks } from "@/lib/wiki-link";
import { formatArticleDate } from "@/lib/format-date";


export const Route = createFileRoute("/bogmaerker")({
  head: () => ({
    meta: [
      { title: "Min samling – Flâneriet" },
      { name: "description", content: "Dine gemte tekster, opskrifter og videoer – samlet ét sted til den langsomme opmærksomhed." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Flâneriet" },
      { property: "og:title", content: "Min samling – Flâneriet" },
      { property: "og:description", content: "Dine gemte tekster, opskrifter og videoer." },
      { property: "og:url", content: "https://flaneriet.online/bogmaerker" },
      { property: "og:image", content: "https://flaneriet.online/og/og-bogmaerker.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Flâneriet – Min samling" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Min samling – Flâneriet" },
      { name: "twitter:description", content: "Dine gemte tekster, opskrifter og videoer." },
      { name: "twitter:image", content: "https://flaneriet.online/og/og-bogmaerker.png" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://flaneriet.online/bogmaerker" }],
  }),
  component: BogmaerkerPage,
});


function BogmaerkerPage() {
  const { markRead, isRead } = useReadArticles();
  const { idsOfKind, count } = useBookmarks();
  const session = useSession();

  const savedArticleIds = idsOfKind("article");
  const savedRecipeIds = idsOfKind("recipe");
  const savedVideoIds = idsOfKind("video");

  const articles = useQuery({
    queryKey: ["articles", { hidePaywall: false }],
    queryFn: () => fetchArticles({ hidePaywall: false }),
    enabled: savedArticleIds.size > 0,
  });
  const recipes = useQuery({
    queryKey: ["recipes"],
    queryFn: fetchRecipes,
    enabled: savedRecipeIds.size > 0,
  });
  const videos = useQuery({
    queryKey: ["videos"],
    queryFn: () => fetchVideos(),
    enabled: savedVideoIds.size > 0,
  });

  const articleList = useMemo(
    () => (articles.data ?? []).filter((a: any) => savedArticleIds.has(a.id)),
    [articles.data, savedArticleIds],
  );
  const recipeList = useMemo(
    () => (recipes.data ?? []).filter((r: any) => savedRecipeIds.has(r.id)),
    [recipes.data, savedRecipeIds],
  );
  const videoList = useMemo(
    () => (videos.data ?? []).filter((v: any) => savedVideoIds.has(v.id)),
    [videos.data, savedVideoIds],
  );

  const totalCount = articleList.length + recipeList.length + videoList.length;
  const isEmpty = count === 0;

  return (
    <div>
      <header className="pt-12 pb-6 border-b-[3px] border-b-double border-b-ink">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-2.5">Din samling</p>
            <h1 className="font-serif font-normal text-[2.4rem] md:text-[3rem] leading-[1.02] text-ink m-0">
              Min samling
            </h1>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-between gap-3 py-6 flex-wrap">
        <p className="meta italic">
          {session ? (
            <>Synket til <em>{session.user.email}</em>. Følger med på tværs af enheder.</>
          ) : (
            <>Opbevares lokalt i din browser. <Link to="/auth" className="underline decoration-rule underline-offset-2 hover:text-bordeaux hover:decoration-bordeaux transition-colors">Log ind</Link> for at synke.</>
          )}
          {totalCount > 0 && (
            <>
              {" · "}
              {[
                articleList.length && `${articleList.length} ${articleList.length === 1 ? "tekst" : "tekster"}`,
                recipeList.length && `${recipeList.length} ${recipeList.length === 1 ? "opskrift" : "opskrifter"}`,
                videoList.length && `${videoList.length} ${videoList.length === 1 ? "video" : "videoer"}`,
              ].filter(Boolean).join(" · ")}
            </>
          )}
        </p>
        {session && (
          <button
            type="button"
            onClick={() => supabase.auth.signOut()}
            className="eyebrow text-ink-soft hover:text-bordeaux transition-colors"
          >
            Log ud
          </button>
        )}
      </div>

      {isEmpty && (
        <p className="meta italic py-16 text-center">
          Her er stille endnu. Klik på "+ Gem" på en tekst, opskrift eller video for at samle den her.
        </p>
      )}

      {articleList.length > 0 && (
        <section className="min-w-0 pt-2 pb-10">
          <header className="flex items-baseline justify-between gap-4 flex-wrap pb-6 mb-2 border-b-2 border-ink">
            <div>
              <p className="eyebrow mb-2">Tekster · gemt</p>
              <h2 className="font-serif font-normal text-[1.7rem] md:text-[2rem] leading-[1.05] text-ink m-0">
                Artikler
              </h2>
            </div>
            <p className="meta italic">{articleList.length} {articleList.length === 1 ? "tekst" : "tekster"}</p>
          </header>
          {articleList.map((a: any, i: number) => {
            const fmt = detectFormat(a.original_url, a.title);
            const kicker = (
              <span className="inline-flex items-center gap-2">
                {TOPIC_LABEL[a.topic as Topic]}
                {fmt !== "article" ? <span className="text-accent-dark not-italic"> · {formatLabel(fmt)}</span> : null}
                <PaywallBadge paywall={a.paywall} />
              </span>
            );
            const sourceDomain = (() => {
              try { return new URL(a.original_url).hostname.replace(/^www\./, ""); } catch { return a.source ?? ""; }
            })();
            const sourceMeta = (
              <>
                {a.source ?? sourceDomain}
                {a.created_at ? ` · ${formatArticleDate(a.created_at)}` : ""}
              </>
            );
            return (
              <ArticleListRow
                key={a.id}
                id={a.id}
                kicker={kicker}
                title={a.title}
                href={a.original_url}
                imageUrl={a.image_url ?? null}
                reason={a.selection_reason ? renderWithWikiLinks(a.selection_reason) : (a.summary ?? undefined)}
                sourceMeta={sourceMeta}
                outLabel={`Læs hos ${a.source ?? sourceDomain}`}
                onOpen={() => markRead(a.id)}
                variant={i === 0 ? "lead" : "row"}
                unread={!isRead(a.id)}
              />
            );
          })}
        </section>
      )}

      {recipeList.length > 0 && (
        <section className="min-w-0 pt-8 pb-10 border-t-[3px] border-t-double border-t-ink">
          <header className="flex items-baseline justify-between gap-4 flex-wrap pb-6 mb-2 border-b-2 border-ink">
            <div>
              <p className="eyebrow mb-2">Køkkenet · gemt</p>
              <h2 className="font-serif font-normal text-[1.7rem] md:text-[2rem] leading-[1.05] text-ink m-0">
                Opskrifter
              </h2>
            </div>
            <p className="meta italic">{recipeList.length} {recipeList.length === 1 ? "opskrift" : "opskrifter"}</p>
          </header>
          <ImageCardGrid
            saveKind="recipe"
            items={recipeList.map((r: any): ImageCardItem => ({
              id: r.id,
              kicker: (
                <span className="inline-flex items-center gap-2">
                  Opskrift
                  <PaywallBadge paywall={r.paywall} />
                </span>
              ),
              title: r.title,
              description: r.summary || undefined,
              meta: (
                <>
                  {r.chef ?? r.source}
                  {r.chef && r.source && r.chef !== r.source ? ` · ${r.source}` : ""}
                </>
              ),
              imageUrl: r.image_url,
              ratio: "4/3",
              href: r.original_url ?? undefined,
            }))}
          />
        </section>
      )}

      {videoList.length > 0 && (
        <section className="min-w-0 pt-8 pb-10 border-t-[3px] border-t-double border-t-ink">
          <header className="flex items-baseline justify-between gap-4 flex-wrap pb-6 mb-2 border-b-2 border-ink">
            <div>
              <p className="eyebrow mb-2">Skærmen · gemt</p>
              <h2 className="font-serif font-normal text-[1.7rem] md:text-[2rem] leading-[1.05] text-ink m-0">
                Videoer
              </h2>
            </div>
            <p className="meta italic">{videoList.length} {videoList.length === 1 ? "video" : "videoer"}</p>
          </header>
          <ImageCardGrid
            saveKind="video"
            items={videoList.map((v: any): ImageCardItem => {
              const secs = v.duration_seconds ?? 0;
              let dur: string | null = null;
              let durBadge: string | null = null;
              if (secs > 0) {
                const totalM = Math.floor(secs / 60);
                const sec = secs % 60;
                if (totalM >= 60) {
                  const h = Math.floor(totalM / 60);
                  dur = `${h} t ${totalM % 60} min`;
                  durBadge = `${h}:${String(totalM % 60).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
                } else {
                  dur = `${totalM} min`;
                  durBadge = `${totalM}:${String(sec).padStart(2, "0")}`;
                }
              }
              const provider = v.provider === "youtube" ? "YouTube" : "Vimeo";
              return {
                id: v.id,
                kicker: (
                  <>
                    {TOPIC_LABEL[v.topic as Topic] ?? "Video"}
                    <span className="mx-1.5 opacity-40">·</span>
                    {provider}
                  </>
                ),
                title: v.title,
                description: v.description || undefined,
                meta: (
                  <>
                    {v.channel ?? provider}
                    {dur ? <span className="text-accent"> · {dur}</span> : null}
                  </>
                ),
                imageUrl: v.thumbnail_url,
                ratio: "16/9",
                play: true,
                duration: durBadge,
                href: v.url,
              };
            })}
          />
        </section>
      )}
    </div>
  );
}
